/**
 * Debug Logger Hook
 *
 * Provides a centralized way to manage debug console logging in ELT components
 * using the UI_ENABLE_DEBUGGER feature flag.
 *
 * Optimized for performance - only re-renders when debug config changes.
 *
 * Usage:
 *   const debugLog = useDebugLogger();
 *   debugLog(1, 'Basic debug message');
 *   debugLog(2, 'More detailed debug info', { data: someObject });
 *   debugLog(3, 'Verbose debug info', { data: someObject });
 */

import { useMemo, useCallback } from 'react';
import { useFeatureValue } from './useFeatureFlags';
import { DebugConfig, getFeatureFlagValue } from 'new/constants/featureFlags';

export function useDebugLogger() {
  const debugConfig: DebugConfig = useFeatureValue('UI_ENABLE_DEBUGGER');

  /**
   * Debug logging function that respects the feature flag configuration
   * Memoized to prevent recreation on every render
   * @param level - Debug level (1-3, where 1 is basic, 2 is more info, 3 is all debug info)
   * @param message - Debug message to log
   * @param data - Optional data object to log alongside the message
   */
  const debugLog = useCallback(
    (level: number, message: string, data?: any) => {
      // If debug is disabled, don't log anything
      if (!debugConfig.enabled) {
        return;
      }

      // If the requested level is higher than the configured debug level, don't log
      if (level > debugConfig.debug_level) {
        return;
      }

      // Log the message with appropriate formatting
      const prefix = `[DEBUG-${level}]`;
      if (data !== undefined) {
        console.log(`${prefix} ${message}`, data);
      } else {
        console.log(`${prefix} ${message}`);
      }
    },
    [debugConfig.enabled, debugConfig.debug_level],
  );

  /**
   * Memoized debug logger object to prevent unnecessary re-renders
   * Only changes when debugConfig changes
   */
  const debugLogger = useMemo(
    () => ({
      debugLog,
      isDebugEnabled: debugConfig.enabled,
      getDebugLevel: () => debugConfig.debug_level,
      shouldLog: (level: number) => debugConfig.enabled && level <= debugConfig.debug_level,
    }),
    [debugConfig.enabled, debugConfig.debug_level, debugLog],
  );

  return debugLogger;
}

/**
 * Non-React version for use in non-component contexts
 * Uses the global GrowthBook instance to get the feature flag value
 */
export function getDebugLogger() {
  const debugConfig: DebugConfig = getFeatureFlagValue('UI_ENABLE_DEBUGGER');

  const debugLog = (level: number, message: string, data?: any) => {
    if (!debugConfig.enabled) {
      return;
    }

    if (level > debugConfig.debug_level) {
      return;
    }

    const prefix = `[DEBUG-${level}]`;
    if (data !== undefined) {
      console.log(`${prefix} ${message}`, data);
    } else {
      console.log(`${prefix} ${message}`);
    }
  };

  return {
    debugLog,
    isDebugEnabled: debugConfig.enabled,
    getDebugLevel: () => debugConfig.debug_level,
    shouldLog: (level: number) => debugConfig.enabled && level <= debugConfig.debug_level,
  };
}
